// apps/accounts/src/pages/SiteBills.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { formatDate } from '../utils/dateHelpers';
import pb from '../lib/pocketbaseClient';
import { cn } from '../lib/utils';
import toast from 'react-hot-toast';

export default function SiteBills() {
  const [bills, setBills] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    billDate: new Date().toISOString().split('T')[0],
    project: '',
    vendor: '',
    measurements: [],
    status: 'draft',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [billsRes, projectsRes, vendorsRes, measurementsRes] = await Promise.all([
        pb.collection('site_bills').getFullList({ sort: '-billDate', expand: 'project,vendor,measurements' }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
        pb.collection('vendors').getFullList({ sort: 'name' }),
        pb.collection('site_measurements').getFullList({ sort: '-measurementDate', expand: 'project,vendor,category' }),
      ]);
      setBills(billsRes);
      setProjects(projectsRes);
      setVendors(vendorsRes);
      setMeasurements(measurementsRes);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const selected = measurements.filter(m => formData.measurements.includes(m.id));
      const totalClient = selected.reduce((sum, m) => sum + (m.clientAmount || 0), 0);
      const totalVendor = selected.reduce((sum, m) => sum + (m.vendorAmount || 0), 0);
      const totalProfit = selected.reduce((sum, m) => sum + (m.profit || 0), 0);

      const data = {
        billDate: formData.billDate,
        project: formData.project,
        vendor: formData.vendor,
        measurements: formData.measurements,
        totalClientAmount: totalClient,
        totalVendorAmount: totalVendor,
        totalProfit: totalProfit,
        status: formData.status,
        createdBy: pb.authStore.model?.id,
      };

      if (editingId) {
        await pb.collection('site_bills').update(editingId, data);
        toast.success('Bill updated successfully');
      } else {
        await pb.collection('site_bills').create(data);
        toast.success('Bill created successfully');
      }

      handleClose();
      fetchData();
    } catch (error) {
      setFormError(error.message || 'Failed to save bill');
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this bill?')) return;
    try {
      await pb.collection('site_bills').delete(id);
      toast.success('Bill deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete bill');
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      billDate: new Date().toISOString().split('T')[0],
      project: '',
      vendor: '',
      measurements: [],
      status: 'draft',
    });
    setFormError('');
    setFormLoading(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      approved: 'bg-green-100 text-green-700',
      paid: 'bg-blue-100 text-blue-700',
    };
    return colors[status] || colors.draft;
  };

  const totalClientAmount = bills.reduce((sum, b) => sum + (b.totalClientAmount || 0), 0);
  const totalVendorAmount = bills.reduce((sum, b) => sum + (b.totalVendorAmount || 0), 0);
  const totalProfit = bills.reduce((sum, b) => sum + (b.totalProfit || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Site Bills</h1>
          <p className="text-sm text-slate-500">Generate bills from site measurements</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => setShowModal(true)}>
          + New Bill
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Bills</p>
            <p className="text-2xl font-bold text-slate-800">{bills.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Client Amount</p>
            <p className="text-2xl font-bold text-green-600">Rs.{totalClientAmount.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Profit</p>
            <p className="text-2xl font-bold text-green-600">Rs.{totalProfit.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600">Date</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600">Project</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600">Vendor</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-right">Client Amount</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-right">Vendor Amount</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-right">Profit</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {bills.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-500">No bills found.</td></tr>
              ) : (
                bills.map(bill => (
                  <tr key={bill.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{formatDate(bill.billDate)}</td>
                    <td className="px-4 py-3 font-medium">{bill.expand?.project?.name || 'N/A'}</td>
                    <td className="px-4 py-3">{bill.expand?.vendor?.name || 'N/A'}</td>
                    <td className="px-4 py-3 text-right font-bold text-green-600">Rs.{bill.totalClientAmount?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-yellow-600">Rs.{bill.totalVendorAmount?.toLocaleString()}</td>
                    <td className={cn("px-4 py-3 text-right font-bold", bill.totalProfit >= 0 ? "text-green-600" : "text-red-600")}>
                      {bill.totalProfit >= 0 ? '+' : ''}Rs.{bill.totalProfit?.toLocaleString()}
                    </td>
                    <td className="px-4 py-3"><Badge className={getStatusColor(bill.status)}>{bill.status}</Badge></td>
                    <td className="px-4 py-3 text-center space-x-1">
                      <Button variant="ghost" size="sm" className="text-amber-600" onClick={() => { setEditingId(bill.id); setFormData({ billDate: bill.billDate, project: bill.project, vendor: bill.vendor, measurements: bill.measurements, status: bill.status }); setShowModal(true); }}>Edit</Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(bill.id)}>Delete</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{editingId ? 'Edit Bill' : 'New Bill'}</h2>
            {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">{formError}</div>}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bill Date *</label>
                  <Input type="date" value={formData.billDate} onChange={(e) => setFormData({...formData, billDate: e.target.value})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Project *</label>
                  <select value={formData.project} onChange={(e) => setFormData({...formData, project: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white" required>
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Vendor *</label>
                <select value={formData.vendor} onChange={(e) => setFormData({...formData, vendor: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white" required>
                  <option value="">Select Vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Select Measurements *</label>
                <div className="border rounded-lg p-4 max-h-60 overflow-y-auto space-y-2">
                  {measurements.filter(m => m.project === formData.project && m.vendor === formData.vendor).map(m => (
                    <label key={m.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.measurements.includes(m.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, measurements: [...formData.measurements, m.id]});
                          } else {
                            setFormData({...formData, measurements: formData.measurements.filter(id => id !== m.id)});
                          }
                        }}
                      />
                      <div className="flex-1">
                        <span className="font-medium">{m.expand?.category?.name}</span>
                        <span className="text-sm text-slate-500 ml-2">{m.totalQuantity} {m.expand?.category?.unit}</span>
                        <span className="text-sm text-slate-400 ml-2">Client: Rs.{m.clientAmount}</span>
                        <span className="text-sm text-slate-400 ml-2">Vendor: Rs.{m.vendorAmount}</span>
                      </div>
                    </label>
                  ))}
                </div>
                {formData.measurements.length > 0 && (
                  <div className="mt-2 text-sm text-slate-500">Selected: {formData.measurements.length} measurements</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white">
                  <option value="draft">Draft</option>
                  <option value="approved">Approved</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              {formData.measurements.length > 0 && (
                <div className="bg-green-50 p-4 rounded-lg border">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Summary</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-slate-500">Client Total</p>
                      <p className="text-lg font-bold text-green-600">
                        Rs.{measurements.filter(m => formData.measurements.includes(m.id)).reduce((sum, m) => sum + (m.clientAmount || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Vendor Total</p>
                      <p className="text-lg font-bold text-yellow-600">
                        Rs.{measurements.filter(m => formData.measurements.includes(m.id)).reduce((sum, m) => sum + (m.vendorAmount || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Total Profit</p>
                      <p className="text-lg font-bold text-green-600">
                        Rs.{measurements.filter(m => formData.measurements.includes(m.id)).reduce((sum, m) => sum + (m.profit || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}